import $ from 'cash-dom';
import DeviceSizes from '../../components/DeviceSizes';

/**
 * Handles dynamic bullets limit updates after vendor filtering
 */
class BulletsLimitHandler {
	constructor() {
		this._bulletsLimitsConfig = null;
		this._initialized = false;
	}

	/**
	 * Initialize the handler
	 */
	init() {
		if (this._initialized) {
			return;
		}

		this._loadBulletsLimitsConfig();
		this._attachEventListeners();
		this._initialized = true;
	}

	/**
	 * Load bullets limits configuration from the admin panel
	 */
	_loadBulletsLimitsConfig() {
		const $filtersHolder = $('.filters-holder');

		if (!$filtersHolder.length) {
			return;
		}

		const bulletsLimits = $filtersHolder.data('bullets-limits');

		if (bulletsLimits && typeof bulletsLimits === 'object') {
			this._bulletsLimitsConfig = bulletsLimits;
		}
	}

	/**
	 * Attach event listeners to filter changes
	 */
	_attachEventListeners() {
		const self = this;

		$(document).on('filters-change', function(e, data) {
			self._handleFiltersChange(data);
		});
	}

	/**
	 * Handle filters change event
	 * @param {Object} data - Event data containing a vendor list
	 */
	_handleFiltersChange(data) {
		if (!data || !data.$vendorsList) {
			return;
		}

		this._updateBulletsForVendors(data.$vendorsList);
	}

	/**
	 * Update bullets for all visible vendors based on their new positions
	 * @param {Cash} $vendorsList - jQuery/Cash collection of vendor elements
	 */
	_updateBulletsForVendors($vendorsList) {
		const self = this;
		let visibleIndex = 0;

		$vendorsList.each(function(i, vendorElement) {
			const $vendor = $(vendorElement);

			if ($vendor.hasClass('hidden')) {
				return;
			}

			self._updateVendorBullets($vendor, visibleIndex);
			visibleIndex++;
		});
	}

	/**
	 * Update bullets for a single vendor
	 * @param {Cash} $vendor - Vendor element
	 * @param {number} newPosition - New position index
	 */
	_updateVendorBullets($vendor, newPosition) {
		let newLimit = this._getBulletsLimitForPosition(newPosition);

		if (newLimit === null || isNaN(newLimit)) {
			return;
		}

		// Update both desktop and mobile bullets
		this._updateBulletsInContainer($vendor.find('.col-bullets.desktop'), newLimit);
		this._updateBulletsInContainer($vendor.find('.col-bullets.mobile'), newLimit);
		this._updateBulletsInContainer($vendor.find('.read-more-content .col-bullets'), newLimit);
	}

	/**
	 * Get bullets limit for a specific position
	 * @param {number} position - Position index (0-based)
	 * @return {number|null} - Bullets limit or null if not configured
	 */
	_getBulletsLimitForPosition(position) {
		if (!this._bulletsLimitsConfig || typeof this._bulletsLimitsConfig !== 'object') {
			return null;
		}

		const configIndex = position;

		const device = this._isMobileDevice() ? 'mobile' : 'desktop';

		if (this._bulletsLimitsConfig[configIndex] && this._bulletsLimitsConfig[configIndex][device]) {
			const limit = parseInt(this._bulletsLimitsConfig[configIndex][device], 10);
			return isNaN(limit) ? null : limit;
		}

		return null;
	}

	/**
	 * Detect if the current device is mobile or tablet
	 * @return {boolean}
	 */
	_isMobileDevice() {
		return window.innerWidth < DeviceSizes.MD
	}

	/**
	 * Update bullets in a specific container
	 * @param {Cash} $container - Container with bullets
	 * @param {number} limit - Maximum number of bullets to show
	 */
	_updateBulletsInContainer($container, limit) {
		if (!$container.length || limit === null) {
			return;
		}

		const $bulletsList = $container.find('ul, ol').first();

		if (!$bulletsList.length) {
			return;
		}

		const $bullets = $bulletsList.find('li');

		if (!$bullets.length) {
			return;
		}

		$bullets.each(function(index, bulletElement) {
			const $bullet = $(bulletElement);

			if (index < limit) {
				$bullet.removeClass('hidden');
			} else {
				$bullet.addClass('hidden');
			}
		});
	}
}

let instance = null;

export default {
	/**
	 * Get a singleton instance
	 * @return {BulletsLimitHandler}
	 */
	getInstance() {
		if (!instance) {
			instance = new BulletsLimitHandler();
		}

		return instance;
	}
};
